/**
 * useFeatureFlags Hook
 *
 * React hook for accessing feature flags and tier detection.
 *
 * @module hooks/useFeatureFlags
 */

import { useMemo } from 'react';
import { FeatureFlags } from '../config/featureFlags';

/**
 * useFeatureFlags Hook
 * @param {Object} user - Current user object
 * @returns {Object} Feature flags and tier information
 */
export const useFeatureFlags = (user = null) => {

  /**
   * Deployment context
   */
  const deploymentContext = useMemo(() => ({
    isStandalone: FeatureFlags.isStandalone(),
    isIntegrated: FeatureFlags.isIntegrated()
  }), []);

  /**
   * Tier detection
   */
  const tier = useMemo(() => ({
    isLite: FeatureFlags.isLiteTier(user),
    isComprehensive: FeatureFlags.isComprehensiveTier(user),
    name: FeatureFlags.isComprehensiveTier(user) ? 'Comprehensive' : 'Lite',
    subscription: user?.subscription || 'standard'
  }), [user]);

  /**
   * Lite tier features (always available)
   */
  const liteFeatures = useMemo(() => ({
    basicWizard: FeatureFlags.features.basicWizard(),
    riskMatrix: FeatureFlags.features.riskMatrix(),
    pdfExport: FeatureFlags.features.pdfExport(),
    basicDashboard: FeatureFlags.features.basicDashboard(),
    reviewTracking: FeatureFlags.features.reviewTracking(),
    workboatTasks: FeatureFlags.features.workboatTasks()
  }), []);

  /**
   * Comprehensive tier features
   */
  const comprehensiveFeatures = useMemo(() => ({
    approvalWorkflow: FeatureFlags.features.approvalWorkflow(user),
    crewAcknowledgment: FeatureFlags.features.crewAcknowledgment(user),
    advancedAnalytics: FeatureFlags.features.advancedAnalytics(user),
    merchantVesselTasks: FeatureFlags.features.merchantVesselTasks(user),
    dynamicRA: FeatureFlags.features.dynamicRA(user),
    permitToWork: FeatureFlags.features.permitToWork(user)
  }), [user]);

  /**
   * Integration features
   */
  const integrationFeatures = useMemo(() => ({
    equipmentIntegration: FeatureFlags.features.equipmentIntegration(user),
    incidentIntegration: FeatureFlags.features.incidentIntegration(user),
    drillIntegration: FeatureFlags.features.drillIntegration(user),
    crewIntegration: FeatureFlags.features.crewIntegration(user)
  }), [user]);

  /**
   * All features combined
   */
  const features = useMemo(() => ({
    ...liteFeatures,
    ...comprehensiveFeatures,
    ...integrationFeatures
  }), [liteFeatures, comprehensiveFeatures, integrationFeatures]);

  /**
   * Check if a specific feature is available
   */
  const hasFeature = useMemo(() => (featureName) => {
    if (featureName in liteFeatures) {
      return liteFeatures[featureName];
    }
    if (featureName in comprehensiveFeatures) {
      return comprehensiveFeatures[featureName];
    }
    if (featureName in integrationFeatures) {
      return integrationFeatures[featureName];
    }
    return false;
  }, [liteFeatures, comprehensiveFeatures, integrationFeatures]);

  /**
   * Get upgrade message for a feature
   */
  const getUpgradeMessage = useMemo(() => (featureName) => {
    return FeatureFlags.getUpgradeMessage(featureName);
  }, []);

  /**
   * Get feature description
   */
  const getFeatureDescription = useMemo(() => (featureName) => {
    return FeatureFlags.getFeatureDescription(featureName);
  }, []);

  /**
   * Check if user can upgrade
   */
  const canUpgrade = useMemo(() => {
    return tier.isLite && deploymentContext.isIntegrated;
  }, [tier.isLite, deploymentContext.isIntegrated]);

  /**
   * Get list of locked features
   */
  const lockedFeatures = useMemo(() => {
    if (tier.isComprehensive) return [];

    return Object.entries(comprehensiveFeatures)
      .filter(([_, available]) => !available)
      .map(([name]) => ({
        name,
        description: getFeatureDescription(name),
        upgradeMessage: getUpgradeMessage(name)
      }));
  }, [tier.isComprehensive, comprehensiveFeatures, getFeatureDescription, getUpgradeMessage]);

  /**
   * Get list of available features
   */
  const availableFeatures = useMemo(() => {
    return Object.entries(features)
      .filter(([_, available]) => available)
      .map(([name]) => name);
  }, [features]);

  return {
    // Deployment context
    ...deploymentContext,

    // Tier information
    tier,
    isLiteTier: tier.isLite,
    isComprehensiveTier: tier.isComprehensive,

    // Feature groups
    liteFeatures,
    comprehensiveFeatures,
    integrationFeatures,
    features,

    // Feature checks
    hasFeature,
    getUpgradeMessage,
    getFeatureDescription,

    // Upgrade information
    canUpgrade,
    lockedFeatures,
    availableFeatures,

    // Helper for conditional rendering
    renderIfFeature: (featureName, component) => {
      return hasFeature(featureName) ? component : null;
    },

    // Helper for upgrade prompts
    showUpgradePrompt: (featureName) => {
      return !hasFeature(featureName) && canUpgrade;
    }
  };
};

export default useFeatureFlags;
